"use client";

import * as Dialog from "@radix-ui/react-dialog";
import type { ReactNode } from "react";
import { cn } from "./cn";

export interface ModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: ReactNode;
  size?: "sm" | "md" | "lg";
  footer?: ReactNode;
  children: ReactNode;
}

/**
 * Generic modal for a short, focused, cancelable action. For a destructive
 * confirmation, reuse ConfirmDialog instead — it already wires the Danger
 * button and short body layout this contract expects.
 */
export function Modal({
  open,
  onOpenChange,
  title,
  description,
  size = "md",
  footer,
  children,
}: ModalProps) {
  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="ui-modal-overlay" />
        <Dialog.Content
          className={cn("ui-modal-content", size)}
          {...(description ? {} : { "aria-describedby": undefined })}
        >
          <div className="ui-modal-header">
            <Dialog.Title asChild>
              <h2>{title}</h2>
            </Dialog.Title>
            {description ? (
              <Dialog.Description asChild>
                <p>{description}</p>
              </Dialog.Description>
            ) : null}
          </div>
          <div className="ui-modal-body">{children}</div>
          {footer ? <div className="ui-modal-footer">{footer}</div> : null}
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
